export type DependentAccessLevel = "full" | "limited" | "view-only";

export interface Dependent {
  id: string;
  name: string;
  relationship: string;
  age: number;
  accessLevel: DependentAccessLevel;
  // Not yet returned by listDependentsApi (the list endpoint only sends the
  // display shape above) — populated once the backend's dependent detail
  // includes them, so the Edit modal can pre-fill exactly what was saved
  // instead of guessing from `name`/`age`.
  firstName?: string;
  lastName?: string;
  email?: string | null;
  dob?: string | null;
}

export type CaregiverAccessStatus = "active" | "pending" | "expired" | "declined" | "revoked";

export interface CaregiverAccess {
  id: string;
  name: string | null;
  email: string;
  status: CaregiverAccessStatus;
  categories: string[];
  expiresDate: string | null; // ISO date, null = no expiration
  // Not yet returned by listCaregiversApi — when the backend starts sending
  // it, the Manage Access panel's "Caregiver since" line picks it up for free.
  grantedDate?: string | null;
}

/** An incoming caregiver-access invite from the *viewer's* side — someone
 * shared their records with you and you haven't reviewed it yet. */
export interface AccessRequest {
  id: string;
  patientId: string;
  patientName: string;
  categories: string[];
  requestedAt: string; // ISO datetime
  expiresDate: string | null;
}

/** A caregiver-initiated request (Direction A) awaiting this patient's
 * review — the reverse of AccessRequest above. No categories yet: the
 * caregiver never picks them; accepting grants full access, narrowable
 * afterward via Manage Access. */
export interface IncomingPatientAccessRequest {
  id: string;
  caregiverName: string;
  requestedAt: string; // ISO datetime
}

/** Resolved from an invite email link's token, before any account exists to
 * authenticate as — lets the signup page pre-fill/lock the email field and
 * show who's inviting. */
export interface InvitePreview {
  email: string;
  patientName: string;
  categories: string[];
  expiresDate: string | null;
}

// 1:1 with the 8 FHIR resource types the backend can actually scope caregiver
// access to (see MedSyncIQBackend's app/modules/patient/family_schemas.py) —
// friendly label -> the internal value the API expects.
export const CAREGIVER_CATEGORY_OPTIONS = [
  { value: "Encounter", label: "Encounters", description: "Visits, admissions, appointments" },
  { value: "Condition", label: "Conditions", description: "Diagnoses and ongoing conditions" },
  { value: "MedicationRequest", label: "Medications", description: "Prescriptions, dosages, refills" },
  { value: "AllergyIntolerance", label: "Allergies", description: "Known allergies and intolerances" },
  { value: "Observation", label: "Observations", description: "Vitals, lab results, measurements" },
  { value: "DiagnosticReport", label: "Diagnostic Reports", description: "X-rays, MRIs, scans, reports" },
  { value: "Procedure", label: "Procedures", description: "Surgeries and other procedures" },
  { value: "Immunization", label: "Immunizations", description: "Vaccination history" },
] as const;

export type CaregiverCategoryValue = (typeof CAREGIVER_CATEGORY_OPTIONS)[number]["value"];
